import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed } from 'vue';
import GLiveStream from '@/components/liveStream/gLiveStream/GLiveStream.vue';
import type { UseLiveStreamEvent, UseLiveStreamProvider } from '@/composable/liveStream/useLiveStream';
import { BrandType, LicenseType, LiveStreamProvider, PlayerType, SportType } from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';
import { useToggleStore } from '@/store/toggleStore';

export function useGLiveStream(event: Ref<UseLiveStreamEvent | null>): UseLiveStreamProvider {
    const { gLiveStreamRestrictions, isGLiveStreamEnabled } = storeToRefs(useToggleStore());
    const { licenseType, playerType, brandType, sboCurrency, ipCountryCode } = storeToRefs(useCustomerStore());

    const gLiveValidRestrictionGroups = computed(() => gLiveStreamRestrictions.value
        .map(rawGroup => parseGLiveGroupRestrict(rawGroup))
        .filter(group => validate(group.licenses, LicenseType[licenseType.value])
        && validate(group.userTypes, PlayerType[playerType.value])
        && validate(group.brandTypes, BrandType[brandType.value])
        && validate(group.currencies, sboCurrency.value)
        && validate(group.countries, ipCountryCode.value)));

    const allowGLiveStream = (leagueSize: string) => {
        if (!isGLiveStreamEnabled.value) return false;

        return gLiveValidRestrictionGroups.value.some(group => validate(group.leagueSizes, leagueSize));
    };

    const isAvailable = computed(() => !!(event.value?.stream?.gLive
        && allowGLiveStream(event.value.league.size)));

    const isPlayable = computed(() => {
        if (!isAvailable.value) return false;
        if (event.value?.sportType === SportType.Soccer) return true;
        return !!event.value?.stream?.gLive?.isPlaying;
    });

    const streamDisplay = computed(() => {
        if (!isPlayable.value || !event.value?.stream?.gLive) return null;

        return {
            provider: LiveStreamProvider.GLive,
            component: GLiveStream,
            key: event.value.stream.gLive.eventId.toString(),
            props: {
                gLive: event.value.stream.gLive,
            },
        };
    });

    return {
        isAvailable,
        isPlayable,
        streamDisplay,
    };
}

type gLivesGroupRestrictionType = {
    licenses: string[];
    userTypes: string[];
    currencies: string[];
    countries: string[];
    leagueSizes: string[];
    brandTypes: string[];
};

function validate(restriction: string[], value: string): boolean {
    return restriction.includes('*') || restriction.some(x => x.toUpperCase() === value?.toUpperCase());
}

function parseGLiveGroupRestrict(rawGroup: string): gLivesGroupRestrictionType {
    // rawGroup format = `License:UserType:Currency:IpCountry:LeagueSize:BrandType`
    const [license, userType, currencies, countries, leagueSizes, brandTypes] = rawGroup?.toUpperCase().split(':') ?? [];
    return {
        licenses: license?.split(',') ?? [],
        userTypes: userType?.split(',') ?? [],
        currencies: currencies?.split(',') ?? [],
        countries: countries?.split(',') ?? [],
        leagueSizes: leagueSizes?.split(',') ?? [],
        brandTypes: brandTypes?.split(',') ?? [],
    };
}
